import React, { MutableRefObject, RefObject, useEffect, useRef } from 'react';
import cn from 'classnames';
import ErrorBoundary, {
    ErrorFallback,
} from '@zmedia/bm-soccer-ui/modules/shared/components/ErrorBoundary';
import { useDispatch, useSelector } from 'react-redux';
import Header from './header';
import { IBreadcrumb, ISection } from '@zmedia/bm-soccer-ui/interface';
import {
    getLiveMatches,
    shouldUpdateLiveMatch,
} from '@zmedia/bm-soccer-ui/redux/actions/match';
import { LocalStore, UrlUtils } from '@zmedia/bm-soccer-ui/utils';
import useAuth from '@zmedia/bm-soccer-ui/hooks/useAuth';
import dynamic from 'next/dynamic';
import { Element, Container, Columns } from '@zmedia/bm-soccer-ui/components';
import { BreadCrumb } from './breadcrumb';
import { LSidebar } from './sidebar';
import { useLocation, usePrevious } from 'react-use';
import { isUpdateMatchSelector } from '@zmedia/bm-soccer-ui/selectors/match';
import { useRouter } from 'next/router';
import Footer from './footer';
import styles from './layout.module.scss';
import { LSTORAGE_KEY } from '../constants';
import AuthService from '../service/AuthService';

const TopProgressBar = dynamic(() => import('./TopProgressBar'), {
    ssr: false,
});

interface IProps {
    children: React.ReactElement;
    showMenu?: boolean;
    sectionTop?: ISection<any>[];
    sidebar?: any;
    breadcrumb?: IBreadcrumb;
    page?: string;
    prZoneIds?: string[];
    options?: any;
    showFormSearch?: boolean;
    adMasthead?: any;
    ad?: any;
    isMobile?: boolean;
}

function LayoutApp({
    sidebar,
    breadcrumb,
    children,
    options = {},
    page = '',
    isMobile,
}: IProps) {
    const { auth } = useAuth();
    const dispatch = useDispatch();
    const layoutRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const { className } = options;
    const router = useRouter();
    const location = useLocation();
    const prevLocation = usePrevious(location);
    const isUpdateLiveMatch = useSelector(isUpdateMatchSelector);

    useEffect(() => {
        const html = document.getElementsByTagName('html')[0];
        html.setAttribute('name', 'soccer');
        return () => {
            html.removeAttribute('name');
        };
    }, []);

    useEffect(() => {
        if (isUpdateLiveMatch) {
            dispatch(getLiveMatches());
        }
    }, [isUpdateLiveMatch]);

    useEffect(() => {
        if (
            location.trigger === 'popstate' &&
            prevLocation?.trigger === 'pushstate'
        ) {
            router.replace(location.href || '');
        }
    }, [location.pathname]);

    useEffect(() => {
        if (location.trigger !== 'load') {
            dispatch(shouldUpdateLiveMatch(false));
            clearTimeout((window as any).timeoutLive);
        }
    }, [location]);

    useEffect(() => {
        (window as any)._BM.isMobile = isMobile;
    }, [isMobile]);

    useEffect(() => {
        const uid = UrlUtils.getParamUrl('uid');
        if (uid) {
            LocalStore.set(LSTORAGE_KEY.BM_UID, uid);
        }
    }, []);

    useEffect(() => {
        const oauthCode = UrlUtils.getParamUrl('code');
        const codeVerify = UrlUtils.getParamUrl('state');
        if (oauthCode && codeVerify) {
            AuthService.loginByZalo(oauthCode, codeVerify)
                .then((resp) => {
                    if (resp.data) {
                        auth();
                        LocalStore.set('refresh', JSON.stringify(true)); // lưu giá trị của form vào localStorage dưới key 'refresh'
                        LocalStore.remove('refresh');
                    }
                })
                .catch((resp) => {
                    console.log(123123, 'err', resp);
                })
                .finally(() => {
                    (window as any).close();
                });
        } 
    }, []);

    useEffect(() => {
        function receiveMessage(e: any) {
            if (e.key === 'refresh') {
                const message = JSON.parse(e.newValue); // chỉ check với key là 'refresh'
                if (message) {
                    setTimeout(() => {
                        (window as any).fnCallbackLogin();
                    }, 500);
                }
            }
        }
        window.addEventListener('storage', receiveMessage);
        return () => window.removeEventListener('storage', receiveMessage);
    }, []);

    return (
        <Element name="soccer">
            <TopProgressBar />
            <div
                className={cn(styles['layout-app'], {
                    [styles[`page-${page}`]]: page,
                    [className]: className,
                })}
                ref={layoutRef}
                id="layout"
            >
                <Header page={page} />
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Element id="content-main">
                        <Container className={styles['main-container']}>
                            {breadcrumb && (
                                <BreadCrumb breadcrumb={breadcrumb} />
                            )}
                            {sidebar ? (
                                <Columns multiline={false}>
                                    <Columns.Column type="sub">
                                        <LSidebar sidebar={sidebar} />
                                    </Columns.Column>
                                    <Columns.Column type="main">
                                        {children}
                                    </Columns.Column>
                                </Columns>
                            ) : (
                                <Columns>
                                    <Columns.Column type="full">
                                        {children}
                                    </Columns.Column>
                                </Columns>
                            )}
                        </Container>
                    </Element>
                </ErrorBoundary>
                <Footer />
            </div>
        </Element>
    );
}

export default LayoutApp;
